import {
  Button,
  Td,
  Tr,
  Tooltip,
  Stack,
  Text,
  Flex,
  Icon,
  useColorModeValue,
  Image,
  Box,
} from '@chakra-ui/react'
import { AttachmentIcon } from '@chakra-ui/icons'
import { QueryDocumentSnapshot } from 'firebase/firestore'
import { Link } from 'react-router-dom'
import FirestoreUser, {
  shortDiagnosisTitles,
  shortRoleTitles,
} from '../types/FirestoreUser'
import ReportTag from './ReportTag'
import StudiesList from './StudiesList'
import useConfigList from '../hooks/useConfigList'
import { formatDateString } from '../utils/formatDate'
import { formatPhoneDisplay } from '../utils/formatPhone'
import { MdCalendarToday } from 'react-icons/md'
import { ReportInfo } from '../types/ReportTypes'

interface UserTableRowProps {
  doc: QueryDocumentSnapshot<FirestoreUser>
  lastNameFirst: boolean
  reports?: Record<string, ReportInfo>
  rowId?: string
  emailFirst?: boolean
}

/**
 * Table row component for a single user
 */
const UserTableRow = ({
  doc,
  lastNameFirst,
  reports = {},
  rowId,
  emailFirst = false,
}: UserTableRowProps) => {
  const deceasedReasons = useConfigList('deceasedReasons') as Record<
    string,
    string
  >

  const verifiedColor = useColorModeValue('green.500', 'green.300')
  const unverifiedColor = useColorModeValue('orange.500', 'orange.300')
  const p2Color = useColorModeValue('blue.500', 'blue.300')
  const p3Color = useColorModeValue('teal.500', 'teal.300')

  const {
    profile,
    patientId,
    email,
    roles = [],
    studies = [],
    lockedDate,
    archived,
    additionalDetails,
    medicalDetails,
  } = doc.data()
  // Debug: when viewing a specific user, log their additionalDetails for inspection
  if (!profile) return null

  const yearOfDeath = lockedDate
    ? lockedDate.substring(0, 4)
    : profile.yearOfDeath || 'Unknown'

  const causeOfDeath = profile.causeOfDeath
    ? deceasedReasons[profile.causeOfDeath] || profile.causeOfDeath
    : 'Unknown'

  return (
    <Tr key={doc.id} id={rowId}>
      {emailFirst ? (
        <>
          <Td>{email}</Td>
          <Td>
            <Link to={`/admin/users/${doc.id}`}>
              <Flex align='center'>
                <Button variant='link'>
                  {lastNameFirst
                    ? `${profile.lastName}, ${profile.firstName}`
                    : `${profile.firstName} ${profile.lastName}`}
                </Button>
                {profile.diagnosisDocumentation && (
                  <>
                    <Tooltip
                      label={
                        profile.diagnosisVerified ? 'Verified' : 'Not verified'
                      }
                      hasArrow
                      placement='top'
                      openDelay={300}
                    >
                      <Icon
                        as={AttachmentIcon}
                        ml={2}
                        color={
                          profile.diagnosisVerified
                            ? verifiedColor
                            : unverifiedColor
                        }
                        aria-label={
                          profile.diagnosisVerified
                            ? 'Validated diagnosis document'
                            : 'Not validated diagnosis document'
                        }
                      />
                    </Tooltip>
                    {profile.diagnosisDateVerified && (
                      <Tooltip
                        label={
                          profile.diagnosisDateVerificationDate
                            ? `Diagnosis date verified ${formatDateString(
                                profile.diagnosisDateVerificationDate
                              )}`
                            : 'Diagnosis date verified'
                        }
                        hasArrow
                        placement='top'
                        openDelay={300}
                      >
                        <Box ml={2} display='inline-flex'>
                          <Icon
                            as={MdCalendarToday}
                            color={verifiedColor}
                            aria-label='Diagnosis date verified'
                          />
                        </Box>
                      </Tooltip>
                    )}
                  </>
                )}
                {profile.agreedToProfileCheckbox && (
                  <Tooltip
                    label='Profile step 1 completed'
                    hasArrow
                    placement='top'
                    openDelay={300}
                  >
                    <Box
                      ml={2}
                      boxSize='5'
                      bg={verifiedColor}
                      color='white'
                      borderRadius='full'
                      display='inline-flex'
                      alignItems='center'
                      justifyContent='center'
                      fontSize='xs'
                      fontWeight='bold'
                      aria-label='Profile agreed'
                    >
                      P1
                    </Box>
                  </Tooltip>
                )}
                {(() => {
                  const shouldShow =
                    additionalDetails?.isComplete ||
                    (additionalDetails &&
                      additionalDetails.isComplete === undefined)

                  return shouldShow ? (
                    <Tooltip
                      label='Profile step 2 completed'
                      hasArrow
                      placement='top'
                      openDelay={300}
                    >
                      <Box
                        ml={2}
                        boxSize='5'
                        bg={p2Color}
                        color='white'
                        borderRadius='full'
                        display='inline-flex'
                        alignItems='center'
                        justifyContent='center'
                        fontSize='xs'
                        fontWeight='bold'
                        aria-label='Profile step 2'
                      >
                        P2
                      </Box>
                    </Tooltip>
                  ) : null
                })()}
                {medicalDetails && (
                  <Tooltip
                    label='Profile step 3 completed'
                    hasArrow
                    placement='top'
                    openDelay={300}
                  >
                    <Box
                      ml={2}
                      boxSize='5'
                      bg={p3Color}
                      color='white'
                      borderRadius='full'
                      display='inline-flex'
                      alignItems='center'
                      justifyContent='center'
                      fontSize='xs'
                      fontWeight='bold'
                      aria-label='Profile step 3'
                    >
                      P3
                    </Box>
                  </Tooltip>
                )}
                {lockedDate && (
                  <Tooltip
                    label={
                      <Stack spacing={1} p={1}>
                        <Text>
                          <strong>Year of Death:</strong>{' '}
                          {yearOfDeath}
                        </Text>
                        <Text>
                          <strong>Cause of Death:</strong>{' '}
                          {causeOfDeath}
                        </Text>
                      </Stack>
                    }
                    hasArrow
                    placement='top'
                    closeOnClick={false}
                    openDelay={300}
                  >
                    <Image
                      src='/cross-icon.jpg'
                      alt='User is deceased'
                      boxSize={5}
                      ml={2}
                      display='inline'
                      borderRadius='full'
                      mixBlendMode='multiply'
                    />
                  </Tooltip>
                )}
                {archived && (
                  <Tooltip
                    label='User is archived and cannot log in'
                    hasArrow
                    placement='top'
                    openDelay={300}
                  >
                    <Text
                      ml={2}
                      px={2}
                      py={1}
                      bg='orange.100'
                      color='orange.600'
                      fontSize='xs'
                      fontWeight='bold'
                      borderRadius='md'
                      display='inline-block'
                    >
                      ARCHIVED
                    </Text>
                  </Tooltip>
                )}
              </Flex>
            </Link>
          </Td>
        </>
      ) : (
        <Td>
          <Link to={`/admin/users/${doc.id}`}>
            <Flex align='center'>
              <Button variant='link'>
                {lastNameFirst
                  ? `${profile.lastName}, ${profile.firstName}`
                  : `${profile.firstName} ${profile.lastName}`}
              </Button>
              {profile.diagnosisDocumentation && (
                <>
                  <Tooltip
                    label={
                      profile.diagnosisVerified ? 'Verified' : 'Not verified'
                    }
                    hasArrow
                    placement='top'
                    openDelay={300}
                  >
                    <Icon
                      as={AttachmentIcon}
                      ml={2}
                      color={
                        profile.diagnosisVerified
                          ? verifiedColor
                          : unverifiedColor
                      }
                      aria-label={
                        profile.diagnosisVerified
                          ? 'Validated diagnosis document'
                          : 'Not validated diagnosis document'
                      }
                    />
                  </Tooltip>
                  {profile.diagnosisDateVerified && (
                    <Tooltip
                      label={
                        profile.diagnosisDateVerificationDate
                          ? `Diagnosis date verified ${formatDateString(
                              profile.diagnosisDateVerificationDate
                            )}`
                          : 'Diagnosis date verified'
                      }
                      hasArrow
                      placement='top'
                      openDelay={300}
                    >
                      <Box ml={2} display='inline-flex'>
                        <Icon
                          as={MdCalendarToday}
                          color={verifiedColor}
                          aria-label='Diagnosis date verified'
                        />
                      </Box>
                    </Tooltip>
                  )}
                </>
              )}
              {profile.agreedToProfileCheckbox && (
                <Tooltip
                  label='User agreed to profile'
                  hasArrow
                  placement='top'
                  openDelay={300}
                >
                  <Box
                    ml={2}
                    boxSize='5'
                    bg={verifiedColor}
                    color='white'
                    borderRadius='full'
                    display='inline-flex'
                    alignItems='center'
                    justifyContent='center'
                    fontSize='xs'
                    fontWeight='bold'
                    aria-label='Profile agreed'
                  >
                    P1
                  </Box>
                </Tooltip>
              )}
              {(() => {
                const shouldShow =
                  additionalDetails?.isComplete ||
                  (additionalDetails &&
                    additionalDetails.isComplete === undefined)

                return shouldShow ? (
                  <Tooltip
                    label='Profile step 2 completed'
                    hasArrow
                    placement='top'
                    openDelay={300}
                  >
                    <Box
                      ml={2}
                      boxSize='5'
                      bg={p2Color}
                      color='white'
                      borderRadius='full'
                      display='inline-flex'
                      alignItems='center'
                      justifyContent='center'
                      fontSize='xs'
                      fontWeight='bold'
                      aria-label='Profile step 2'
                    >
                      P2
                    </Box>
                  </Tooltip>
                ) : null
              })()}
              {medicalDetails && (
                <Tooltip
                  label='Profile step 3 completed'
                  hasArrow
                  placement='top'
                  openDelay={300}
                >
                  <Box
                    ml={2}
                    boxSize='5'
                    bg={p3Color}
                    color='white'
                    borderRadius='full'
                    display='inline-flex'
                    alignItems='center'
                    justifyContent='center'
                    fontSize='xs'
                    fontWeight='bold'
                    aria-label='Profile step 3'
                  >
                    P3
                  </Box>
                </Tooltip>
              )}
              {lockedDate && (
                <Tooltip
                  label={
                    <Stack spacing={1} p={1}>
                      <Text>
                        <strong>Year of Death:</strong>{' '}
                        {yearOfDeath}
                      </Text>
                      <Text>
                        <strong>Cause of Death:</strong>{' '}
                        {causeOfDeath}
                      </Text>
                    </Stack>
                  }
                  hasArrow
                  placement='top'
                  closeOnClick={false}
                  openDelay={300}
                >
                  <Image
                    src='/cross-icon.jpg'
                    alt='User is deceased'
                    boxSize={5}
                    ml={2}
                    display='inline'
                    borderRadius='full'
                    mixBlendMode='multiply'
                  />
                </Tooltip>
              )}
              {archived && (
                <Tooltip
                  label='User is archived and cannot log in'
                  hasArrow
                  placement='top'
                  openDelay={300}
                >
                  <Text
                    ml={2}
                    px={2}
                    py={1}
                    bg='orange.100'
                    color='orange.600'
                    fontSize='xs'
                    fontWeight='bold'
                    borderRadius='md'
                    display='inline-block'
                  >
                    ARCHIVED
                  </Text>
                </Tooltip>
              )}
            </Flex>
          </Link>
        </Td>
      )}
      <Td>{patientId}</Td>
      <Td>
        <StudiesList studies={studies} />
      </Td>
      <Td>
        <ReportTag userId={doc.id} reports={reports} />
      </Td>
      <Td>
        {profile.diagnosis ? shortDiagnosisTitles[profile.diagnosis] : ''}
      </Td>
      <Td>{formatDateString(profile.diagnosisVerificationDate)}</Td>
      <Td>{roles.map((r) => shortRoleTitles[r] || r).join('/')}</Td>
      <Td>{formatPhoneDisplay(profile.phoneNumber, profile.phoneCountryCode)}</Td>
      {!emailFirst && <Td>{email}</Td>}
      <Td>{profile.country}</Td>
      <Td>{profile.state}</Td>
      <Td>{profile.city}</Td>
    </Tr>
  )
}

export default UserTableRow
